import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Collapse,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  DialogTitle,
  InputAdornment,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EditIcon from '@mui/icons-material/Edit';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  useCalcularTaxaMutation,
  useCancelarPedidoAguardandoPagamentoMutation,
  useGetEnderecosQuery,
  useGetFormasPagamentoLojaQuery,
  useGetHorariosLojaQuery,
  useGetLojaAtualQuery,
  useGetMeQuery,
  useLazyValidarIdentificadorSalaoQuery,
  useValidarCupomMutation,
  type CupomValidado,
  type Endereco,
  type MotivoSemTaxa,
  type PayloadNovoPedido,
} from '../../api';
import { montarLinkWhatsapp } from '../../lib/contato';
import type { RootState } from '../../store';
import { limparCarrinho } from '../../store/carrinho-slice'
import { useAcaoUnica } from '../../lib/use-acao-unica'
import {
  rastrearIniciouCheckout,
  rastrearInformouPagamento,
  rastrearPedidoConcluido,
  type ItemRastreio,
} from '../../lib/tracking';
import { avancarNoEnter } from '../../lib/foco-formulario';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { LgpdAviso } from '../../components/lgpd-aviso';
import { DialogPagamentoOnline } from '../../components/dialog-pagamento-online';
import { DialogCancelarPagamento } from '../../components/dialog-cancelar-pagamento';
import { registrarPedidoLocal } from '../../lib/pedidos-locais';
import { DialogTroco } from '../../components/dialog-troco';
import { descreverTroco, lerValorTroco, trocoInsuficiente } from '../../lib/troco';
import { canalDoIdentificador } from '../../lib/canal';
import { montarPayloadPedido } from '../../lib/payload-pedido';
import { useEnviarPedido } from '../../lib/enviar-pedido';
import { metodosDaForma } from '../../lib/tef/metodos-da-forma';
import { IconePagamento } from '../../components/icone-pagamento';
import { useImagensPorProduto, useTiposCalculoPorPasso } from '../../lib/imagens-catalogo';
import { itensCarrinhoParaListagem } from '../../lib/itens-pedido';
import { ListaItensPedido } from '../../components/lista-itens-pedido';
import { formatarBRL } from '../../lib/formato';
import { agendamentoOfertavel as calcularAgendamentoOfertavel, antecedenciaAgendamentoMin, gerarDiasAgendaveis } from '../../lib/agendamento'
import { CampoComanda } from '../../components/campo-comanda';
import { DialogLogin } from '../../components/dialog-login';
import { EscolhaEntrar } from '../../components/escolha-entrar';
import { FormEndereco, type EnderecoPreenchido } from '../../components/form-endereco';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { getTabletConfig } from '../../lib/tablet-config';
import { ESTILO_ACAO_KIOSK, TemaKiosk } from '../../components/catalogo-kiosk/tema-kiosk';
import { contextoSalao } from '../../lib/salao';
import ScreenHeader from '../totem/checkout/components/screen-header';
import { medidasDoAparelho } from '../catalogo/kiosk/medidas';
import { cpfValido } from '../../lib/cpf'
import { ehCupomDeFrete, freteNaTela, textoDoCupomDeFrete, TEXTO_DO_CUPOM_FORA, TEXTO_FRETE_ALTERADO, type FreteNaTela } from '../../lib/frete-gratis'
import { AvisoCashbackPendente } from './aviso-cashback-pendente'
import { BlocoCashback } from './bloco-cashback'
import { fecharValoresDoPedido, type ValoresDoPedidoNaTela } from './cashback-no-checkout'
import { envioPodeTerChegado, reacaoAoErroDoPedido, TEXTO_CONFIRA_DEPOIS_DO_CUPOM } from './erros-cashback'
import { useCashbackNoCheckout } from './use-cashback-no-checkout'

const LS_DADOS_MESA = 'cardapio-sischef:dados-mesa';
// Telefone de contato do delivery: o usuario_cardapio não tem telefone no
// cadastro (login é por e-mail), mas a loja precisa de um contato no pedido.
const LS_TELEFONE_DELIVERY = 'cardapio-sischef:telefone-delivery';
/** Pedido sem login: nome e endereço ficam no aparelho para o próximo pedido. */
const LS_SEM_LOGIN = 'cardapio-sischef:delivery-sem-login';

interface DadosSemLogin {
  nome: string;
  endereco: EnderecoPreenchido | null;
}

function lerDadosSemLogin(idConfig: number): DadosSemLogin {
  try {
    const raw = localStorage.getItem(`${LS_SEM_LOGIN}:${idConfig}`);
    if (raw) return JSON.parse(raw) as DadosSemLogin;
  } catch {
    /* ignora */
  }
  return { nome: '', endereco: null };
}

function salvarDadosSemLogin(idConfig: number, dados: DadosSemLogin): void {
  try {
    localStorage.setItem(`${LS_SEM_LOGIN}:${idConfig}`, JSON.stringify(dados));
  } catch {
    /* ignora */
  }
}

const PASSOS = ['Entrega', 'Pagamento', 'Confirmação'];
// TOTEM (autoatendimento): wizard próprio com a escolha de consumo e a
// identificação em passos separados — cada tela pede uma coisa só, ideal pro
// teclado virtual e pro cliente em pé (padrão de quiosque). Passos em ./passos-totem.

interface DadosLocais {
  nome: string;
  telefone: string;
  cpf: string;
}

function carregarDadosLocais(idConfig: number): DadosLocais {
  try {
    const raw = localStorage.getItem(`${LS_DADOS_MESA}:${idConfig}`);
    if (raw) return JSON.parse(raw) as DadosLocais;
  } catch {
    /* ignora */
  }
  return { nome: '', telefone: '', cpf: '' };
}

function salvarDadosLocais(idConfig: number, dados: DadosLocais): void {
  localStorage.setItem(`${LS_DADOS_MESA}:${idConfig}`, JSON.stringify(dados));
}

export function CheckoutFinalPage(): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: loja, refetch: recarregarLoja } = useGetLojaAtualQuery()
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  // Dados do usuário e endereços só existem logado — gateamos por token. Assim,
  // ao logar pelo DialogLogin (sem remontar o checkout), o token entra na store
  // primeiro, o skip cai e as queries reexecutam já com o Bearer (sem 401 nem
  // corrida de token).
  const { data: usuario } = useGetMeQuery(undefined, {
    skip: !loja || loja.identificador.tipo !== 'DELIVERY' || !accessToken,
  });
  const { data: enderecos } = useGetEnderecosQuery(undefined, {
    skip: !loja || loja.identificador.tipo !== 'DELIVERY' || !accessToken,
  });
  const { data: formasPagamento } = useGetFormasPagamentoLojaQuery(undefined, { skip: !loja });
  const { itens, observacaoGeral } = useSelector((s: RootState) => s.carrinho);

  const ehDelivery = loja?.identificador.tipo === 'DELIVERY';
  // Fotos do resumo do pedido — o carrinho guarda só o id do produto.
  const canalDoCatalogo = loja ? canalDoIdentificador(loja.identificador) : 'CARDAPIO_DELIVERY'
  const imagensCatalogo = useImagensPorProduto(canalDoCatalogo)
  const tiposCalculo = useTiposCalculoPorPasso(canalDoCatalogo)
  // Mesa/comanda: pedido vai para a tela de mesa/comanda do Sischef sem
  // pagamento nem cupom (acertado com o garçom).
  const ehMesaComanda = loja?.identificador.tipo === 'MESA' || loja?.identificador.tipo === 'COMANDA';
  const ehTablet = loja?.identificador.tipo === 'TABLET';
  // Config "Solicitar comanda?": COMANDA = pergunta a comanda; senão = mesa do aparelho.
  const tabletModo: 'MESA' | 'COMANDA' = loja?.configuracao.tabletIdentificador === 'COMANDA' ? 'COMANDA' : 'MESA';
  const mesaTablet = ehTablet ? getTabletConfig().mesa.trim() : '';
  const secundario = loja?.configuracao.tipoIdentificadorSecundario;
  const secundarioObrigatorio = !!loja?.configuracao.identificadorSecundarioObrigatorio;
  // A conta é chaveada pelo PRINCIPAL. Se o QR não é do tipo principal (ex.: QR de
  // mesa numa loja de comanda), pedimos o principal (comanda) e o QR vira o secundário.
  const ctxSalao = contextoSalao(loja);
  const pedirPrincipalMesa = ehMesaComanda && !ctxSalao.principalConhecido;
  // Mesa/comanda do QR que o cliente leu — o que dá para afirmar sem depender do
  // que ainda será digitado. (A legenda antiga rotulava esse número com o tipo do
  // identificador PRINCIPAL, o que mentia quando o QR era o secundário.)
  const destinoDoPedido =
    ehMesaComanda && loja?.identificador.numero?.trim()
      ? { tipo: loja.identificador.tipo as 'MESA' | 'COMANDA', numero: loja.identificador.numero.trim() }
      : null;

  // Formas de retirada habilitadas na loja (string ex.: "DLC").
  const formaRetirada = (loja?.configuracao.formaRetirada ?? 'D').toUpperCase();
  const permiteReceber = formaRetirada.includes('D');
  const permiteRetirar = formaRetirada.includes('L');

  const { data: horariosResp } = useGetHorariosLojaQuery(undefined, { skip: !ehDelivery });

  // Passo do wizard do delivery: 0 entrega, 1 pagamento, 2 confirmação.
  const [passo, setPasso] = useState(0);
  // Começa sem escolha: o cliente é obrigado a marcar receber ou retirar.
  const [metodoEntrega, setMetodoEntrega] = useState<'RECEBER' | 'RETIRAR' | null>(null);
  const [agendar, setAgendar] = useState(false);
  const [editandoEndereco, setEditandoEndereco] = useState(false);
  const [dataAgendada, setDataAgendada] = useState('');
  const [faixaAgendada, setFaixaAgendada] = useState('');
  const [cupomAberto, setCupomAberto] = useState(false);

  // Dados do cliente (mesa: do form/localStorage; delivery: do usuario logado)
  const [dadosMesa, setDadosMesa] = useState<DadosLocais>({ nome: '', telefone: '', cpf: '' });
  const [telefoneDelivery, setTelefoneDelivery] = useState(() => localStorage.getItem(LS_TELEFONE_DELIVERY) ?? '');
  // CPF do delivery — exigido só quando a forma escolhida é ONLINE (ASAAS exige cpfCnpj).
  const [cpfDelivery, setCpfDelivery] = useState('');
  const [identificadorSecundario, setIdentificadorSecundario] = useState('');
  // TABLET modo COMANDA: comanda perguntada no checkout (a mesa vem do aparelho).
  const [comanda, setComanda] = useState('');
  // Mesa/comanda que não existe no Sischef — mostrado no próprio campo (ver conferirSalao).
  const [erroComanda, setErroComanda] = useState<string | null>(null);
  const [validarIdentificador] = useLazyValidarIdentificadorSalaoQuery();
  const refComanda = useRef<HTMLInputElement>(null);
  // Foca o campo da comanda ao abrir o checkout (autoFocus em Dialog nem sempre pega).
  useEffect(() => {
    if (!ehTablet || tabletModo !== 'COMANDA') return undefined;
    const t = setTimeout(() => refComanda.current?.focus(), 150);
    return () => clearTimeout(t);
  }, [ehTablet, tabletModo]);
  // MESA/COMANDA (celular do cliente): abre já no primeiro campo que falta, com o
  // teclado aberto. Quem nunca pediu nesta loja cai no Nome; quem já pediu tem os
  // dados salvos no aparelho e cai direto no número da mesa/comanda, que é o
  // único campo que muda a cada visita.
  const refNomeMesa = useRef<HTMLInputElement>(null);
  const refTelefoneMesa = useRef<HTMLInputElement>(null);
  const refIdentificadorMesa = useRef<HTMLInputElement>(null);
  const jaFocouMesa = useRef(false);
  const [idEnderecoEntrega, setIdEnderecoEntrega] = useState<number | ''>('');
  // Identidade da forma é o `id` (único) — o `codigo` colide entre offline/online
  // (ex.: PIX local e PIX ASAAS têm o mesmo codigo).
  const [idFormaPagamento, setIdFormaPagamento] = useState<number | ''>('');
  // Vazio = não precisa de troco. Aceita vírgula porque o teclado do celular oferece.
  const [trocoInput, setTrocoInput] = useState('');
  // Dinheiro exige resposta explícita no DialogTroco — "não preciso" também conta.
  const [trocoRespondido, setTrocoRespondido] = useState(false);
  const [trocoAberto, setTrocoAberto] = useState(false);
  const [cupomInput, setCupomInput] = useState(() => new URLSearchParams(window.location.search).get('cupom') ?? '');
  const [cupomAplicado, setCupomAplicado] = useState<CupomValidado | null>(null);
  const [taxa, setTaxa] = useState<{
    valor: number;
    encontrado: boolean;
    distanciaKm?: number;
    estrategia: 'BAIRRO' | 'KM';
    motivo?: MotivoSemTaxa;
  } | null>(null);
  const [rodadaDoFrete, setRodadaDoFrete] = useState(0)
  /** Recusa da loja por distância — não é erro do cliente nem do sistema. */
  const [foraDaArea, setForaDaArea] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** O pedido não foi porque os valores mudaram (cupom, saldo do cashback): não é falha, o cliente confere e envia de novo. */
  const [avisoDoEnvio, setAvisoDoEnvio] = useState<string | null>(null)
  /** Erro do cupom — renderizado dentro da própria caixa do cupom, não no rodapé. */
  const [erroCupom, setErroCupom] = useState<string | null>(null);
  // Falha no ENVIO do pedido (não em cupom/validação) — habilita o atalho
  // "Voltar ao início", já que o carrinho pode não ter sido limpo.
  const [falhaEnvio, setFalhaEnvio] = useState(false);
  // TOTEM/TEF: pedido criado em AGUARDANDO_PAGAMENTO; abre o dialog que dispara
  // a venda no terminal ELGIN. Só limpa carrinho/navega quando o TEF aprova.
  // DELIVERY/ONLINE: pedido criado em AGUARDANDO_PAGAMENTO; abre o pagamento online (PIX).
  const [onlineAberto, setOnlineAberto] = useState<{
    pedidoId: string;
    valorCentavos: number;
    metodos: string[];
  } | null>(null);
  // Login no meio do checkout: abre como modal sobre o checkout (não navega), pra
  // o cliente voltar exatamente ao passo em que estava ao concluir.
  const [loginAberto, setLoginAberto] = useState(false);
  // Pedido sem login (só se a loja permitir): o cliente escolheu seguir sem conta.
  const [semLogin, setSemLogin] = useState(false);
  const [nomeSemLogin, setNomeSemLogin] = useState('');
  const [enderecoSemLogin, setEnderecoSemLogin] = useState<EnderecoPreenchido | null>(null);
  const [editandoEnderecoSemLogin, setEditandoEnderecoSemLogin] = useState(false);
  // Cancelar pagamento online: confirma antes de descartar o pedido e zerar o carrinho.
  const [confirmarCancelOnline, setConfirmarCancelOnline] = useState(false);

  const [calcularTaxa, statusTaxa] = useCalcularTaxaMutation();
  const { enviar: enviarAoServidor, descartarChave, enviando: enviandoAoServidor } = useEnviarPedido();
  const [cancelarPedidoOnline] = useCancelarPedidoAguardandoPagamentoMutation();
  const [validarCupom, statusCupom] = useValidarCupomMutation();
  // A trava cobre também as esperas antes do POST (cupom, conferência da mesa), onde o isLoading ainda é false.
  const [enviarPedido, enviandoPedido] = useAcaoUnica(confirmar)
  const [confirmarCancelamentoOnline, cancelandoOnline] = useAcaoUnica(desistirDoPagamentoOnline)
  const [escolherOutraForma, trocandoForma] = useAcaoUnica(trocarFormaDoPagamentoOnline)
  // Cashback como desconto: só no delivery, em loja com o uso ligado.
  const lojaAceitaCashback = ehDelivery && loja?.configuracao.cashbackUsoAtivo === true
  const cashback = useCashbackNoCheckout({ lojaAceitaCashback, clienteLogado: !!accessToken, idUsuario: usuario?.id })

  // Itens do carrinho no formato dos eventos de rastreamento (Pixel/GA4).
  const itensRastreio: ItemRastreio[] = itens.map((i) => ({
    id: i.idProduto,
    nome: i.nome,
    valor: i.precoUnitario,
    quantidade: i.quantidade,
  }));

  // Dispara InitiateCheckout uma vez, ao abrir o checkout com itens.
  const checkoutRastreado = useRef(false);
  useEffect(() => {
    if (checkoutRastreado.current || itens.length === 0) return;
    checkoutRastreado.current = true;
    const valor = itens.reduce((s, i) => s + i.precoUnitario * i.quantidade, 0);
    rastrearIniciouCheckout(
      itens.map((i) => ({ id: i.idProduto, nome: i.nome, valor: i.precoUnitario, quantidade: i.quantidade })),
      valor,
    );
  }, [itens]);

  // Dispara AddPaymentInfo uma vez, quando há forma de pagamento definida.
  const pagamentoRastreado = useRef(false);
  useEffect(() => {
    if (pagamentoRastreado.current || !idFormaPagamento) return;
    pagamentoRastreado.current = true;
    const valor = itens.reduce((s, i) => s + i.precoUnitario * i.quantidade, 0);
    const forma = (formasPagamento ?? []).find((fp) => fp.id === idFormaPagamento)?.descricao;
    rastrearInformouPagamento(
      itens.map((i) => ({ id: i.idProduto, nome: i.nome, valor: i.precoUnitario, quantidade: i.quantidade })),
      valor,
      forma,
    );
  }, [idFormaPagamento, itens, formasPagamento]);

  const theme = useTheme();
  // Desktop: o checkout abre como modal centralizado; mobile: tela cheia.
  const ehDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Totem e tablet: a tela é do aparelho, não do navegador — a finalização usa
  // tudo, com o conteúdo numa coluna legível no meio (o modal de 51% da largura
  // vinha logo depois de um cardápio em tela cheia).
  const ehKiosk = ehTablet;
  const fechar = (): void => {
    navigate('/carrinho');
  };

  // Marca que o localStorage já foi lido: até então `dadosMesa` está vazio e
  // pareceria que falta tudo (o foco iria para o Nome mesmo em cliente conhecido).
  const [dadosMesaCarregados, setDadosMesaCarregados] = useState(false);
  useEffect(() => {
    // Totem é dispositivo compartilhado: nunca pré-preenche dados do cliente anterior.
    if (!loja || ehDelivery) return;
    setDadosMesa(carregarDadosLocais(loja.configuracao.id));
    setDadosMesaCarregados(true);
  }, [loja, ehDelivery]);

  // Foco inicial do modo mesa/comanda: primeiro campo OBRIGATÓRIO que está vazio.
  // Campo opcional não entra — abrir o teclado por cima de algo que o cliente pode
  // pular atrapalha mais do que ajuda. Roda uma vez só (`jaFocouMesa`): refazer o
  // foco a cada tecla digitada roubaria o cursor de onde o cliente está.
  useEffect(() => {
    if (!ehMesaComanda || !dadosMesaCarregados || jaFocouMesa.current) return undefined;

    const precisaIdentificador = pedirPrincipalMesa
      ? true
      : secundarioObrigatorio && !!secundario && secundario !== 'NENHUM';

    const alvo = !dadosMesa.nome.trim()
      ? refNomeMesa
      : !dadosMesa.telefone.trim()
        ? refTelefoneMesa
        : precisaIdentificador && !identificadorSecundario.trim()
          ? refIdentificadorMesa
          : null;

    if (!alvo) {
      jaFocouMesa.current = true;
      return undefined;
    }

    // Mesmo atraso dos outros focos da tela: o campo só existe depois da
    // transição de entrada, e focar antes não abre o teclado no celular.
    const t = setTimeout(() => {
      jaFocouMesa.current = true;
      alvo.current?.focus();
    }, 150);
    return () => clearTimeout(t);
  }, [
    ehMesaComanda,
    dadosMesaCarregados,
    dadosMesa.nome,
    dadosMesa.telefone,
    identificadorSecundario,
    pedirPrincipalMesa,
    secundario,
    secundarioObrigatorio,
  ]);

  // Delivery: pré-preenche o telefone com o do perfil (agora persistido no
  // usuario_cardapio), sem sobrescrever o que o cliente já tiver digitado.
  useEffect(() => {
    if (usuario?.telefone) {
      setTelefoneDelivery((atual) => atual.trim() || usuario.telefone!.trim());
    }
  }, [usuario?.telefone]);

  // Delivery: pré-preenche o CPF com o do perfil, mas SÓ se for um CPF válido —
  // assim um valor inválido salvo no perfil (ex.: telefone) não vaza pro campo CPF.
  useEffect(() => {
    if (usuario?.cpf && cpfValido(usuario.cpf)) {
      setCpfDelivery((atual) => atual.trim() || usuario.cpf!.replace(/\D/g, ''));
    }
  }, [usuario?.cpf]);

  useEffect(() => {
    if (!enderecos) return;
    if (idEnderecoEntrega === '') {
      const principalEnd = enderecos.find((e) => e.principal) ?? enderecos[0];
      if (principalEnd) setIdEnderecoEntrega(principalEnd.id);
    }
  }, [enderecos, idEnderecoEntrega]);

  // Faixas agendáveis a partir da grade de horários da loja.
  const tempoMaxPreparo = loja?.configuracao.tempoMaxPreparo;
  const diasAgendaveis = useMemo(
    () =>
      ehDelivery && horariosResp
        ? gerarDiasAgendaveis(horariosResp.horarios, new Date(), {
            leadMin: antecedenciaAgendamentoMin(tempoMaxPreparo),
          })
        : [],
    [ehDelivery, horariosResp, tempoMaxPreparo],
  );

  // Ao marcar "agendar", seleciona o primeiro dia disponível.
  useEffect(() => {
    if (!agendar) return;
    if (!dataAgendada && diasAgendaveis.length > 0) setDataAgendada(diasAgendaveis[0]!.valor);
  }, [agendar, diasAgendaveis, dataAgendada]);

  // Mantém a faixa coerente com o dia selecionado.
  useEffect(() => {
    const dia = diasAgendaveis.find((d) => d.valor === dataAgendada);
    if (dia && !dia.faixas.some((f) => f.rotulo === faixaAgendada)) {
      setFaixaAgendada(dia.faixas[0]?.rotulo ?? '');
    }
  }, [dataAgendada, diasAgendaveis, faixaAgendada]);

  // Loja fechada no delivery com agendamento ofertável: o pedido só pode ser
  // agendado — já liga o agendamento e direciona o cliente pra escolher a faixa.
  useEffect(() => {
    if (!loja || !ehDelivery || horariosResp?.aberta !== false) return;
    const modo = loja.configuracao.agendamentoDelivery
    if (calcularAgendamentoOfertavel({ modo, lojaFechada: true, temDiaAgendavel: diasAgendaveis.length > 0 })) setAgendar(true)
  }, [loja, ehDelivery, horariosResp, diasAgendaveis]);

  // Formas visíveis por canal: ONLINE só no delivery; TEF só no totem; LOCAL sempre.
  const formasVisiveis = (formasPagamento ?? []).filter((fp) => {
    if (fp.tipo === 'ONLINE') return ehDelivery;
    if (fp.tipo === 'TEF') return false;
    return true;
  });

  // Forma selecionada resolvida pelo `id` único.
  const formaPagamentoSel = (formasPagamento ?? []).find((fp) => fp.id === idFormaPagamento);

  // Sem login: na entrega ou PIX online. Cartão online exige conta (o backend recusa na cobrança).
  const formasNaEntrega = formasVisiveis.filter((fp) => fp.tipo !== 'ONLINE');
  const formasPixOnline = formasVisiveis.filter(
    (fp) => fp.tipo === 'ONLINE' && metodosDaForma(fp.forma, fp.metodos).includes('PIX'),
  );
  const formasSemLogin = [...formasNaEntrega, ...formasPixOnline];
  const temCartaoOnline = formasVisiveis.some(
    (fp) => fp.tipo === 'ONLINE' && metodosDaForma(fp.forma, fp.metodos).some((m) => m !== 'PIX'),
  );
  const lojaAceitaSemLogin =
    !!ehDelivery &&
    loja?.configuracao.permitirPedidoSemLogin === true &&
    (!formasPagamento || formasSemLogin.length > 0);
  const escolhendoComoPedir = lojaAceitaSemLogin && !accessToken && !semLogin;
  const pedidoSemLogin = lojaAceitaSemLogin && !accessToken && semLogin;

  // Recupera o que o cliente sem login informou no último pedido nesta loja.
  const idConfigLoja = loja?.configuracao.id;
  useEffect(() => {
    if (!pedidoSemLogin || idConfigLoja == null) return;
    const salvos = lerDadosSemLogin(idConfigLoja);
    setNomeSemLogin((atual) => atual || salvos.nome);
    setEnderecoSemLogin((atual) => atual ?? salvos.endereco);
  }, [pedidoSemLogin, idConfigLoja]);

  // Mesmo formato do endereço salvo: taxa, resumo e payload leem um só objeto.
  const enderecoSemLoginComoSalvo = useMemo<Endereco | undefined>(
    () =>
      enderecoSemLogin
        ? {
          id: 0,
          apelido: null,
          tipo: null,
          cep: enderecoSemLogin.cep ?? null,
          logradouro: enderecoSemLogin.logradouro,
          numero: enderecoSemLogin.numero || null,
          complemento: enderecoSemLogin.complemento ?? null,
          bairro: enderecoSemLogin.bairro,
          cidade: enderecoSemLogin.cidade,
          uf: enderecoSemLogin.uf,
          referencia: null,
          latitude: enderecoSemLogin.latitude != null ? String(enderecoSemLogin.latitude) : null,
          longitude: enderecoSemLogin.longitude != null ? String(enderecoSemLogin.longitude) : null,
          principal: false,
        }
        : undefined,
    [enderecoSemLogin],
  );

  const ehReceber = !!ehDelivery && metodoEntrega === 'RECEBER';
  const enderecoSelecionado = !ehReceber
    ? undefined
    : pedidoSemLogin
      ? enderecoSemLoginComoSalvo
      : enderecos?.find((e) => e.id === idEnderecoEntrega);

  /** Corrigir o endereço: sem login é no próprio checkout; logado, na conta. */
  function ajustarEndereco(): void {
    if (pedidoSemLogin) setEditandoEnderecoSemLogin(true);
    else navigate('/conta', { state: { de: '/checkout' } });
  }

  useEffect(() => {
    setTaxa(null);
    setForaDaArea(null);
    if (!enderecoSelecionado) return;
    void calcularTaxa({
      bairro: { nome: enderecoSelecionado.bairro, municipio: enderecoSelecionado.cidade, uf: enderecoSelecionado.uf },
      // Vai junto porque a loja pode cobrar por distância; qual dos dois vale
      // quem decide é o backend, pela estratégia configurada.
      endereco: {
        logradouro: enderecoSelecionado.logradouro,
        numero: enderecoSelecionado.numero,
        bairro: enderecoSelecionado.bairro,
        municipio: enderecoSelecionado.cidade,
        uf: enderecoSelecionado.uf,
        cep: enderecoSelecionado.cep,
        latitude: enderecoSelecionado.latitude ? Number(enderecoSelecionado.latitude) : null,
        longitude: enderecoSelecionado.longitude ? Number(enderecoSelecionado.longitude) : null,
      },
    })
      .unwrap()
      .then((r) =>
        setTaxa({
          valor: r.valor,
          encontrado: r.encontrado,
          distanciaKm: r.distanciaKm,
          estrategia: r.estrategia,
          motivo: r.motivo,
        }),
      )
      .catch((err) => {
        // Fora da área não é falha: é a loja dizendo que não entrega ali.
        if (codigoDoErro(err) === 'FORA_DA_AREA') {
          setForaDaArea(extrairMsg(err) ?? 'Endereço fora da área de entrega desta loja.');
          return;
        }
        setErro(extrairMsg(err) ?? 'Falha ao calcular taxa');
      });
  }, [enderecoSelecionado, calcularTaxa, rodadaDoFrete])

  const diaSelecionado = diasAgendaveis.find((d) => d.valor === dataAgendada);
  const faixaSelecionada = diaSelecionado?.faixas.find((f) => f.rotulo === faixaAgendada);
  const horarioAgendamentoISO = agendar && faixaSelecionada ? faixaSelecionada.data.toISOString() : undefined;

  if (!loja) return <></>;

  const subtotal = itens.reduce((s, i) => s + i.precoUnitario * i.quantidade, 0);
  const freteDoPedido = freteDoCupom(cupomAplicado)
  const taxaEntrega = freteDoPedido.freteCobrado
  const desconto = cupomAplicado?.valorDesconto ?? 0;
  // Taxa de conveniência: valor fixo do delivery, somado ao total (exibido só na confirmação).
  const taxaConveniencia = ehDelivery ? Math.max(0, Number(loja.configuracao.taxaConveniencia ?? 0)) : 0;
  const valoresDoCashbackNaTela: ValoresDoPedidoNaTela = {
    subtotal,
    descontoDoCupom: desconto,
    taxas: taxaEntrega + taxaConveniencia,
    tipoDaFormaDePagamento: formaPagamentoSel?.tipo,
  }
  const cashbackNaTela = cashback.noPedido(valoresDoCashbackNaTela)
  const valorCashback = cashbackNaTela.valorUsado
  const { total } = fecharValoresDoPedido({ subtotal, taxaEntrega, taxaConveniencia, descontoDoCupom: desconto, valorCashback })
  const totalItens = itens.reduce((s, i) => s + i.quantidade, 0);

  // Troco só faz sentido em dinheiro pago na entrega/retirada. Sem isso o
  // entregador sai sem saber quanto levar (chamado #137).
  const ehDinheiro = formaPagamentoSel?.forma === 'DINHEIRO';
  const trocoNumero = lerValorTroco(trocoInput);
  // Pode ficar inválido depois de respondido: remover cupom ou trocar para entrega sobe o total.
  const trocoInvalido = ehDinheiro && trocoInsuficiente(trocoNumero, total);
  const faltaResponderTroco = ehDinheiro && !trocoRespondido;
  // Sem resposta ou com troco que não cobre mais o total: o Continuar pergunta de novo.
  const trocoPendente = faltaResponderTroco || trocoInvalido;

  function textoTroco(): string | null {
    return ehDinheiro ? descreverTroco(trocoNumero, total) : null;
  }

  function freteDoCupom(cupom: CupomValidado | null): FreteNaTela {
    return freteNaTela({
      entregaEmCasa: ehReceber,
      frete: taxa?.encontrado ? taxa.valor : null,
      cupom,
      subtotal,
      freteGratisAPartirDe: loja?.configuracao.freteGratisAPartirDe,
    })
  }

  function escolherFormaPagamento(id: number, forma: string): void {
    if (forma === 'DINHEIRO') {
      // Voltar ao dinheiro vindo de outra forma pede a resposta de novo (o valor anterior fica sugerido).
      if (idFormaPagamento !== id) setTrocoRespondido(false);
      setTrocoAberto(true);
    }
    setIdFormaPagamento(id);
  }

  // Agendamento no delivery (config da loja): 'SEMPRE' | 'DESATIVADO' | 'SO_FECHADA'.
  const modoAgendamento = loja.configuracao.agendamentoDelivery;
  const lojaFechadaDelivery = ehDelivery && horariosResp?.aberta === false;
  const agendamentoOfertavel =
    ehDelivery &&
    calcularAgendamentoOfertavel({ modo: modoAgendamento, lojaFechada: lojaFechadaDelivery, temDiaAgendavel: diasAgendaveis.length > 0 })
  // Fechada agora: se dá pra agendar, o pedido TEM que ser agendado; senão, não dá pra pedir agora.
  const precisaAgendar = lojaFechadaDelivery && agendamentoOfertavel;
  const fechadaSemPedido = lojaFechadaDelivery && !agendamentoOfertavel;

  async function aplicarCupom(): Promise<void> {
    setErro(null);
    setErroCupom(null);
    try {
      const validado = await validarCupom({ cupom: cupomInput.trim(), subtotal, metodoEntrega: metodoEntrega ?? undefined }).unwrap()
      setCupomAplicado(validado);
    } catch (err) {
      setCupomAplicado(null);
      // Erro de cupom aparece DENTRO da caixa do cupom, ao lado do campo — no
      // rodapé da tela o cliente não relaciona a mensagem ao que ele digitou.
      setErroCupom(extrairMsg(err) ?? 'Cupom inválido');
    }
  }

  function removerCupom(): void {
    setCupomAplicado(null);
    setCupomInput('');
    setErroCupom(null);
  }

  // ===== Validação por passo (delivery) =====
  function passo0Valido(): boolean {
    // O login não é exigido aqui (visitante pode escolher entrega/retirada e
    // avançar); a exigência de conta para delivery é checada no envio
    // (podeSubmeter) e sinalizada no passo de Confirmação.
    // Obrigatório escolher receber ou retirar.
    if (metodoEntrega === 'RECEBER') {
      if (!permiteReceber) return false;
      if (!enderecoSelecionado) return false;
      // A validacao da entrega e uma so: o endereco esta dentro do que a loja atende.
      // Sem taxa calculada nao da para responder isso, entao nao passa — em BAIRRO
      // e em KM. O que muda por estrategia e a MENSAGEM, nao a regra (ver alertas).
      if (!taxa || !taxa.encontrado) return false;
    } else if (metodoEntrega === 'RETIRAR') {
      if (!permiteRetirar) return false;
    } else {
      return false;
    }
    // Loja fechada no delivery: exige agendamento (se ofertável) ou barra o pedido.
    if (fechadaSemPedido) return false;
    if (precisaAgendar && !horarioAgendamentoISO) return false;
    if (agendar && !horarioAgendamentoISO) return false;
    return true;
  }

  function passo1Valido(): boolean {
    if (!idFormaPagamento) return false;
    // Forma ONLINE no delivery exige CPF válido (o ASAAS cobra o cpfCnpj do cliente).
    if (ehDelivery && formaSelecionadaOnline() && !cpfValido(cpfDelivery)) return false;
    // Dinheiro: o cliente respondeu se precisa de troco, e o troco cobre o total.
    if (trocoPendente) return false;
    return true;
  }

  /** A forma selecionada agora é do tipo ONLINE? */
  function formaSelecionadaOnline(): boolean {
    return formaPagamentoSel?.tipo === 'ONLINE';
  }


  function podeSubmeter(): boolean {
    if (itens.length === 0) return false;
    // Delivery: forma de pagamento + conta logada (ou nome, sem login) + endereço/telefone.
    // O aviso de login aparece no passo de Confirmação.
    if (ehDelivery) {
      const identificado = pedidoSemLogin
        ? !!nomeSemLogin.trim() && formasSemLogin.some((fp) => fp.id === idFormaPagamento)
        : !!accessToken && !!usuario;
      return !!idFormaPagamento && identificado && passo0Valido() && passo1Valido() && !!telefoneDelivery.trim();
    }
    // TABLET: anônimo; precisa da mesa configurada e (no modo COMANDA) da comanda.
    if (ehTablet) {
      if (!mesaTablet) return false;
      if (tabletModo === 'COMANDA' && !comanda.trim()) return false;
      return true;
    }
    // Mesa/comanda: sem forma de pagamento nem cupom.
    if (!dadosMesa.nome.trim() || !dadosMesa.telefone.trim()) return false;
    // Caso B (QR ≠ principal): o principal digitado (ex.: comanda) é obrigatório.
    if (pedirPrincipalMesa && !identificadorSecundario.trim()) return false;
    // Caso A: secundário obrigatório, se configurado.
    if (!pedirPrincipalMesa && secundario && secundario !== 'NENHUM' && secundarioObrigatorio && !identificadorSecundario.trim())
      return false;
    return true;
  }

  /**
   * Confere no cadastro do Sischef os números de mesa/comanda que vão no pedido.
   *
   * Mesa entra junto: o pedido que começa pela comanda pergunta a mesa, e mesa
   * errada manda o garçom entregar no lugar errado. Falha de rede não trava o
   * pedido: quem decide é o backend, que confere de novo.
   */
  async function conferirSalao(
    campos: Array<{ numero?: string; tipo: 'MESA' | 'COMANDA' | null }>,
  ): Promise<Array<string | undefined> | null> {
    const conferidos: Array<string | undefined> = [];

    for (const campo of campos) {
      const informado = campo.numero?.trim();
      if (!campo.tipo || !informado) {
        conferidos.push(campo.numero);
        continue;
      }

      const r = await validarIdentificador({ identificador: informado, tipo: campo.tipo })
        .unwrap()
        .catch(() => null);
      if (r && !r.valida) {
        const msg = `${campo.tipo === 'MESA' ? 'Mesa' : 'Comanda'} não encontrada ou inativa. Confira o número com o garçom.`;
        setErroComanda(msg);
        setErro(msg);
        return null;
      }

      // Segue a GRAFIA DO CADASTRO: '001' digitado numa loja que cadastrou '1'
      // vai como '1'. Para o Sischef as duas são contas diferentes, e mandar a
      // digitada abriria uma conta paralela à do garçom.
      conferidos.push(r?.numero ?? informado);
    }

    return conferidos;
  }

  async function confirmar(): Promise<void> {
    setErro(null);
    setAvisoDoEnvio(null)
    setErroComanda(null);
    setFalhaEnvio(false);

    // Cupom digitado mas não aplicado: valida agora, pra não enviar o pedido
    // sem o desconto que o cliente espera.
    // Cupom só existe no delivery (totem e mesa/comanda não usam).
    let cupomFinal = ehDelivery ? cupomAplicado : null;
    if (ehDelivery && cupomInput.trim() && !cupomFinal) {
      try {
        cupomFinal = await validarCupom({ cupom: cupomInput.trim(), subtotal, metodoEntrega: metodoEntrega ?? undefined }).unwrap()
        setCupomAplicado(cupomFinal);
      } catch (err) {
        // Aqui vão as DUAS: a específica na caixa do cupom (que pode estar fora
        // da vista) e a instrução junto do botão de confirmar, que é onde o
        // cliente está olhando.
        setErroCupom(extrairMsg(err) ?? 'Cupom inválido');
        setCupomAberto(true);
        setErro('Cupom inválido — corrija ou limpe o campo para continuar');
        return;
      }
      // O cupom muda quanto de cashback dá para usar: com o switch ligado, o cliente confere o valor novo antes.
      if (cashbackNaTela.valorUsado > 0 || freteDoCupom(cupomFinal).freteCobrado !== freteDoCupom(null).freteCobrado) {
        setAvisoDoEnvio(TEXTO_CONFIRA_DEPOIS_DO_CUPOM)
        return
      }
    }
    const freteFinal = freteDoCupom(cupomFinal)
    const descontoFinal = cupomFinal?.valorDesconto ?? 0;
    const valoresDoCashback: ValoresDoPedidoNaTela = { ...valoresDoCashbackNaTela, descontoDoCupom: descontoFinal }
    const valorCashbackFinal = cashback.noPedido(valoresDoCashback).valorUsado
    const valoresFinais = fecharValoresDoPedido({
      subtotal,
      taxaEntrega,
      taxaConveniencia,
      descontoDoCupom: descontoFinal,
      valorCashback: valorCashbackFinal,
    })
    const totalFinal = valoresFinais.total

    const cliente = ehDelivery
      ? {
        nome: pedidoSemLogin ? nomeSemLogin.trim() : usuario!.nome,
        telefone: telefoneDelivery.trim(),
        cpf: cpfDelivery.replace(/\D/g, '') || usuario?.cpf || undefined,
      }
      : ehTablet
        ? { nome: '', telefone: '' }
        : {
          nome: dadosMesa.nome.trim(),
          telefone: dadosMesa.telefone.trim(),
          cpf: dadosMesa.cpf.trim() || undefined,
        };

    if (ehDelivery) localStorage.setItem(LS_TELEFONE_DELIVERY, telefoneDelivery.trim());
    if (pedidoSemLogin) {
      salvarDadosSemLogin(loja!.configuracao.id, { nome: nomeSemLogin.trim(), endereco: enderecoSemLogin });
    }
    if (!ehDelivery && !ehTablet) salvarDadosLocais(loja!.configuracao.id, dadosMesa);

    /*
     * A escolha consumir no local/levar vai ESTRUTURADA (`consumoTotem`), não mais
     * carimbada no começo da observação: assim ela vira o tipo do pedido no Sischef e
     * aparece no card do PDV com ícone próprio, em vez de virar texto que alguém
     * precisa ler no meio do resto.
     */
    const observacaoFinal = observacaoGeral?.trim() || undefined;

    // TABLET: principal = comanda (modo COMANDA) ou mesa (modo MESA); secundário = mesa (modo COMANDA).
    const tabletPrincipal = ehTablet ? (tabletModo === 'COMANDA' ? comanda.trim() : mesaTablet) : undefined;
    const tabletSecundario = ehTablet && tabletModo === 'COMANDA' ? mesaTablet : undefined;
    // Mesa/comanda: caso B → principal digitado + secundário (mesa) do QR; caso A →
    // principal vem do QR (backend usa o tenant) e só o secundário é digitado.
    const mesaPrincipal = pedirPrincipalMesa ? identificadorSecundario.trim() || undefined : undefined;
    const mesaSecundario = pedirPrincipalMesa
      ? ctxSalao.secundarioNumero || undefined
      : identificadorSecundario.trim() || undefined;
    const semPagamento = ehMesaComanda || ehTablet;

    // Mesa/comanda informada tem que existir no Sischef. O backend recusa de
    // qualquer jeito — a conferência aqui é para o erro aparecer no campo, e não
    // como falha genérica depois de confirmar o pedido inteiro.
    const conferidos = await conferirSalao([
      { numero: tabletPrincipal, tipo: ehTablet ? tabletModo : null },
      { numero: mesaPrincipal, tipo: pedirPrincipalMesa ? ctxSalao.tipoPrincipal : null },
      {
        numero: mesaSecundario,
        // 'NENHUM' é a loja que não usa secundário — não há o que conferir.
        tipo: !ehTablet && !pedirPrincipalMesa && (secundario === 'MESA' || secundario === 'COMANDA') ? secundario : null,
      },
    ]);
    if (!conferidos) return;
    const [tabletPrincipalOk, mesaPrincipalOk, mesaSecundarioOk] = conferidos;

    // O campo passa a mostrar a comanda em que o pedido realmente entrou — sem
    // isto o cliente sai da tela achando que está na '001' e pede a conta dela.
    if (ehTablet && tabletModo === 'COMANDA' && tabletPrincipalOk && tabletPrincipalOk !== comanda.trim()) {
      setComanda(tabletPrincipalOk);
    }

    const payload: PayloadNovoPedido = montarPayloadPedido({
      cliente,
      itens,
      identificadorSecundarioNumero: ehTablet ? tabletSecundario : mesaSecundarioOk,
      identificadorPrincipalNumero: ehTablet ? tabletPrincipalOk : mesaPrincipalOk,
      observacaoGeral: observacaoFinal,
      horarioAgendamento: ehDelivery ? horarioAgendamentoISO : undefined,
      // Retirada só se aplica a delivery (nos demais canais não vai).
      metodoEntrega: ehDelivery ? metodoEntrega ?? undefined : undefined,
      enderecoEntrega: enderecoSelecionado
        ? {
          cep: enderecoSelecionado.cep ?? undefined,
          logradouro: enderecoSelecionado.logradouro,
          numero: enderecoSelecionado.numero ?? undefined,
          complemento: enderecoSelecionado.complemento ?? undefined,
          bairro: enderecoSelecionado.bairro,
          cidade: enderecoSelecionado.cidade,
          uf: enderecoSelecionado.uf,
          referencia: enderecoSelecionado.referencia ?? undefined,
          latitude: enderecoSelecionado.latitude ? Number(enderecoSelecionado.latitude) : undefined,
          longitude: enderecoSelecionado.longitude ? Number(enderecoSelecionado.longitude) : undefined,
        }
        : undefined,
      // Mesa/comanda não enviam forma de pagamento; cupom só no delivery.
      // Envia o id (identidade única) + o codigo (mapeamento Sischef) da forma.
      formaPagamento: semPagamento || !formaPagamentoSel ? null : formaPagamentoSel,
      // Só vai quando é dinheiro e o cliente informou; ausente = sem troco.
      troco: !semPagamento && ehDinheiro && trocoNumero !== null ? trocoNumero : undefined,
      cupom: ehDelivery && !freteFinal.cupomFora ? cupomFinal?.codigo : undefined,
      subtotal,
      // Conveniência embute no frete pro total bater no Sischef (no app é linha à parte).
      taxaEntrega: taxaEntrega + taxaConveniencia,
      freteComDesconto: ehDelivery ? freteFinal.freteComDesconto : undefined,
      desconto: valoresFinais.desconto,
      total: totalFinal,
      valorCashback: valorCashbackFinal,
    });

    const cobraAntes = formaPagamentoSel?.tipo === 'TEF' || formaPagamentoSel?.tipo === 'ONLINE'

    // Depois de um envio que pode ter debitado, o valor do cashback vem da tentativa pendente: sem o cliente mudar
    // nada, o corpo sai igual, a assinatura bate e a chave se repete — só ela reaproveita o débito que pode ter saído.
    let chave = ''

    try {
      const desfecho = await enviarAoServidor({
        payload,
        idUsuario: usuario?.id ?? null,
        cobraAntes,
        // CASHBACK_CHAVE_ENCERRADA: esta chave não debita mais. Vai o mesmo corpo com chave nova, uma vez só.
        trocarChaveSe: (err) => reacaoAoErroDoPedido(codigoDoErro(err)) === 'TROCAR_CHAVE_E_REENVIAR',
        aoUsarChave: (c) => {
          chave = c
        },
      })
      cashback.esquecerTentativa()

      if (desfecho.tipo === 'cancelado') {
        setErro('Este pedido foi cancelado e não pode mais ser pago. Toque em Enviar pedido para fazer um novo.')
        return
      }

      const r = desfecho.pedido

      if (desfecho.tipo === 'cobrar') {
        // Sem login o pedido fica no aparelho já agora: a aba fechada no meio do PIX não o perde mais.
        if (pedidoSemLogin) {
          registrarPedidoLocal(loja!.configuracao.id, {
            id: r.id,
            shortId: r.shortId ?? null,
            dataCriacao: r.dataCriacao ?? new Date().toISOString(),
          });
        }

        setOnlineAberto({
          pedidoId: r.id,
          valorCentavos: Math.round(totalFinal * 100),
          // Sem login o cartão online é recusado pelo backend: sobra o PIX.
          metodos: metodosDaForma(formaPagamentoSel!.forma, formaPagamentoSel!.metodos).filter(
            (m) => !pedidoSemLogin || m === 'PIX',
          ),
        });
        return;
      }

      rastrearPedidoConcluido({ id: r.id, valor: totalFinal, itens: itensRastreio });
      dispatch(limparCarrinho());
      navigate(`/pedido/${r.id}`);
    } catch (err) {
      await tratarErroDoEnvio(err, payload, chave, valoresDoCashback)
    }
  }

  /** Erro no envio do pedido. O que fazer vem do código do erro (`erros-cashback.ts`). */
  async function tratarErroDoEnvio(
    err: unknown,
    payload: PayloadNovoPedido,
    chave: string,
    valoresDoCashback: ValoresDoPedidoNaTela,
  ): Promise<void> {
    const reacao = reacaoAoErroDoPedido(codigoDoErro(err))

    // A mensagem é o aviso do passo de enviar, que fica na tela enquanto a tentativa estiver pendente.
    if (reacao === 'OFERECER_REENVIO' && payload.valorCashback) {
      cashback.guardarTentativa(chave, payload.valorCashback, 'INDISPONIVEL')
      return
    }

    // Sem resposta ou 5xx, o pedido e o débito podem ter acontecido: o valor fica pendente para o reenvio, também
    // depois de recarregar, sair igual e com a mesma chave. O aviso do passo de enviar diz isso ao cliente.
    if (envioPodeTerChegado(err) && payload.valorCashback) {
      cashback.guardarTentativa(chave, payload.valorCashback, 'SEM_RESPOSTA')
      return
    }

    // Recusa do backend (4xx), ou envio sem cashback: não há débito a reaproveitar.
    cashback.esquecerTentativa()

    if (codigoDoErro(err) === 'FRETE_ALTERADO') {
      setRodadaDoFrete((rodada) => rodada + 1)
      await recarregarLoja()
      setAvisoDoEnvio(TEXTO_FRETE_ALTERADO)
      return
    }

    if (reacao === 'ATUALIZAR_SALDO') {
      setAvisoDoEnvio(await cashback.atualizarSaldo(valoresDoCashback))
      return
    }

    if (reacao === 'DESLIGAR_CASHBACK') {
      cashback.desligarEAtualizarSaldo()
      setErro(extrairMsg(err) ?? 'Não foi possível usar seu cashback neste pedido.')
      return
    }

    // MOSTRAR_MENSAGEM, e também a chave encerrada que já foi reenviada uma vez.
    setErro(extrairMsg(err) ?? 'Não foi possível confirmar o pedido')
    setFalhaEnvio(true)
  }

  /** Pago (pelo diálogo ou no meio de uma desistência): limpa o carrinho e abre o pedido. */
  function concluirPagamentoOnline(pedidoId: string, valorCentavos: number, pagoAoCancelar = false): void {
    rastrearPedidoConcluido({ id: pedidoId, valor: valorCentavos / 100, itens: itensRastreio });
    dispatch(limparCarrinho());
    setOnlineAberto(null);
    // O aviso vai no estado da navegação: a tela do pedido é quem o mostra.
    navigate(`/pedido/${pedidoId}`, pagoAoCancelar ? { state: { pagoAoCancelar: true } } : undefined);
  }

  /** Cancela no backend; rede fora ou backend sem a rota não travam o cliente (o prazo cancela depois). */
  async function cancelarNoBackend(pedidoId: string): Promise<'CANCELADO' | 'JA_PAGO' | 'FALHOU'> {
    try {
      await cancelarPedidoOnline(pedidoId).unwrap();
      return 'CANCELADO';
    } catch (err) {
      return codigoDoErro(err) === 'PEDIDO_NAO_AGUARDA_PAGAMENTO' ? 'JA_PAGO' : 'FALHOU';
    }
  }

  async function desistirDoPagamentoOnline(): Promise<void> {
    if (!onlineAberto) return;
    const { pedidoId, valorCentavos } = onlineAberto;
    const resultado = await cancelarNoBackend(pedidoId);
    setConfirmarCancelOnline(false);
    if (resultado === 'JA_PAGO') {
      concluirPagamentoOnline(pedidoId, valorCentavos, true);
      return;
    }
    setOnlineAberto(null);
    dispatch(limparCarrinho());
    navigate('/catalogo');
  }

  /** Desiste deste pagamento mas fica com o carrinho, de volta à escolha da forma. */
  async function trocarFormaDoPagamentoOnline(): Promise<void> {
    if (!onlineAberto) return;
    const { pedidoId, valorCentavos } = onlineAberto;
    const resultado = await cancelarNoBackend(pedidoId);
    if (resultado === 'JA_PAGO') {
      concluirPagamentoOnline(pedidoId, valorCentavos, true);
      return;
    }
    // Chave nova: o próximo envio é outro pedido, não o que acabou de ser cancelado.
    descartarChave();
    setOnlineAberto(null);
    setIdFormaPagamento('');
    setPasso(1);
  }

  const enderecoEmpresa = loja.enderecoEmpresa;

  // ===== Render: blocos de pagamento e cupom (compartilhados) =====
  const blocoPagamento = (
    <div>
      <Typography variant="subtitle1" gutterBottom>
        Forma de pagamento
      </Typography>
      {formasPagamento && formasVisiveis.length === 0 ? (
        <Alert severity="warning">
          A loja ainda não configurou as formas de pagamento. Entre em contato com o estabelecimento.
        </Alert>
      ) : ehDelivery ? (
        <Stack spacing={1}>
          {pedidoSemLogin && temCartaoOnline && formasPixOnline.length === 0 && (
            <Typography variant="subtitle2" color="text.secondary">
              Pagar na entrega
            </Typography>
          )}
          {(pedidoSemLogin ? formasSemLogin : formasVisiveis).map((fp) => {
            const sel = idFormaPagamento === fp.id;
            const online = fp.tipo === 'ONLINE';
            const tef = fp.tipo === 'TEF';
            return (
              <Paper
                key={fp.id}
                variant="outlined"
                onClick={() => escolherFormaPagamento(fp.id, fp.forma)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  borderColor: sel ? 'primary.main' : 'divider',
                  borderWidth: sel ? 2 : 1,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                    <IconePagamento forma={fp.forma} />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography>
                      {fp.descricao}
                      {tef ? ' — na maquininha' : ''}
                    </Typography>
                    {online && (
                      <Typography variant="body2" color="text.secondary">
                        {pedidoSemLogin ? 'PIX — você paga agora pelo app' : 'Pagamento online — você paga agora pelo app'}
                      </Typography>
                    )}
                    {sel && ehDelivery && fp.tipo === 'LOCAL' && fp.forma === 'PIX' && !!fp.pixChave && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Quando a loja confirmar o pedido, mostramos o código Pix. Depois é só enviar o comprovante
                        para a loja.
                      </Typography>
                    )}
                    {sel && fp.forma === 'DINHEIRO' && (
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                        <Typography
                          variant="body2"
                          color={trocoPendente ? 'error' : 'text.secondary'}
                          sx={{ flexGrow: 1 }}
                        >
                          {faltaResponderTroco
                            ? 'Informe se precisa de troco'
                            : trocoInvalido
                              ? 'O total mudou: informe o troco de novo'
                              : textoTroco()}
                        </Typography>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTrocoAberto(true);
                          }}
                          sx={{ flexShrink: 0 }}
                        >
                          {faltaResponderTroco ? 'Informar' : 'Alterar'}
                        </Button>
                      </Stack>
                    )}
                  </Box>
                  <Radio checked={sel} />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <TextField
          select
          fullWidth
          value={idFormaPagamento}
          onChange={(e) => setIdFormaPagamento(Number(e.target.value))}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="" disabled>
            Selecione a forma de pagamento
          </MenuItem>
          {formasVisiveis.map((fp) => (
            <MenuItem key={fp.id} value={fp.id}>
              {fp.descricao}
            </MenuItem>
          ))}
        </TextField>
      )}
      {pedidoSemLogin && temCartaoOnline && (
        <Paper
          variant="outlined"
          sx={{ mt: 2, p: 1.5, bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Pagar agora pelo site
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <LockOutlinedIcon color="action" fontSize="small" />
            <Typography variant="body2" sx={{ flexGrow: 1, lineHeight: 1.3 }}>
              {formasPixOnline.length > 0 ? 'Para pagar com cartão online, entre na sua conta.' : 'Para pagar online, entre na sua conta.'}
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setLoginAberto(true)} sx={{ flexShrink: 0 }}>
              Entrar
            </Button>
          </Stack>
        </Paper>
      )}
      {ehDelivery && formaPagamentoSel?.tipo === 'ONLINE' && (
        <TextField
          label="CPF"
          value={cpfDelivery}
          onChange={(e) => setCpfDelivery(e.target.value.replace(/\D/g, '').slice(0, 11))}
          fullWidth
          required
          sx={{ mt: 2 }}
          error={!!cpfDelivery && !cpfValido(cpfDelivery)}
          helperText={
            !!cpfDelivery && !cpfValido(cpfDelivery) ? 'Informe um CPF válido' : 'Necessário para o pagamento online.'
          }
          inputProps={{ inputMode: 'numeric', maxLength: 11 }}
        />
      )}
    </div>
  );

  const blocoCupom = (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ cursor: 'pointer' }}
        onClick={() => setCupomAberto((v) => !v)}
      >
        <LocalOfferIcon color="action" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography fontWeight={600}>Tem um cupom?</Typography>
          <Typography variant="body2" color="text.secondary">
            Clique e insira o código
          </Typography>
        </Box>
        <ChevronRightIcon color="action" sx={{ transform: cupomAberto ? 'rotate(90deg)' : 'none', transition: '0.2s' }} />
      </Stack>
      <Collapse in={cupomAberto || !!cupomAplicado || !!erroCupom}>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={cupomInput}
            error={!!erroCupom}
            onChange={(e) => {
              setCupomInput(e.target.value.toUpperCase());
              setCupomAplicado(null);
              setErroCupom(null);
            }}
            placeholder="Código do cupom"
          />
          <Button
            variant="outlined"
            onClick={aplicarCupom}
            disabled={!cupomInput.trim() || !!cupomAplicado || statusCupom.isLoading}
            sx={{ flexShrink: 0 }}
          >
            {statusCupom.isLoading ? <CircularProgress size={20} /> : 'Aplicar'}
          </Button>
        </Stack>
        {erroCupom && (
          <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setErroCupom(null)}>
            {erroCupom}
          </Alert>
        )}
        {cupomAplicado && freteDoPedido.cupomFora && (
          <Alert severity="error" sx={{ mt: 1.5 }} onClose={removerCupom}>
            {TEXTO_DO_CUPOM_FORA[freteDoPedido.cupomFora]}
          </Alert>
        )}
        {cupomAplicado && !freteDoPedido.cupomFora && (
          <Alert severity="success" sx={{ mt: 1.5 }} onClose={removerCupom}>
            Cupom <strong>{cupomAplicado.codigo}</strong> aplicado:{' '}
            {ehCupomDeFrete(cupomAplicado.tipoDesconto) ? textoDoCupomDeFrete(cupomAplicado) : `desconto de ${formatarBRL(cupomAplicado.valorDesconto)}`}
            {cupomAplicado.escopo === 'REDE' && ' — válido em todas as lojas da rede'}
          </Alert>
        )}
      </Collapse>
    </Paper>
  );

  const blocoResumoValores = (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        {/* Para onde vai o pedido — frase inteira, junto dos valores, que é onde o
            cliente confere tudo antes de confirmar. Fora deste quadro, no alto da
            tela, a informação ficava solta. */}
        {destinoDoPedido && (
          <>
            <Stack direction="row" alignItems="center" spacing={1}>
              {destinoDoPedido.tipo === 'MESA' ? (
                <TableRestaurantIcon fontSize="small" color="primary" />
              ) : (
                <ReceiptLongIcon fontSize="small" color="primary" />
              )}
              <Typography variant="body2" color="text.secondary">
                Pedido para a {destinoDoPedido.tipo === 'MESA' ? 'mesa' : 'comanda'}{' '}
                <Box component="strong" sx={{ color: 'text.primary' }}>
                  {destinoDoPedido.numero}
                </Box>
              </Typography>
            </Stack>
            <Divider />
          </>
        )}
        <Linha label="Subtotal" valor={subtotal} />
        {ehReceber && <Linha label="Taxa de entrega" valor={taxaEntrega} />}
        {taxaConveniencia > 0 && <Linha label="Taxa de conveniência" valor={taxaConveniencia} />}
        {desconto > 0 && <Linha label="Desconto" valor={-desconto} />}
        {valorCashback > 0 && <Linha label="Cashback usado" valor={-valorCashback} />}
        <Divider />
        <Linha label="Total" valor={total} forte />
      </Stack>
    </Paper>
  );

  // ===== Conteúdo do wizard (delivery) =====
  function renderEntrega(): JSX.Element {
    if (escolhendoComoPedir) {
      return (
        <EscolhaEntrar
          onEntrar={() => setLoginAberto(true)}
          onSemLogin={() => setSemLogin(true)}
          lojaAceitaCashback={lojaAceitaCashback}
        />
      )
    }
    return (
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" color="text.secondary">
          Como você quer receber seu pedido?
        </Typography>

        {permiteReceber && (
          <CartaoOpcao
            selecionado={metodoEntrega === 'RECEBER'}
            onClick={() => setMetodoEntrega('RECEBER')}
            icone={<TwoWheelerIcon />}
            titulo="Receber no seu endereço"
            corpoLargo={
              metodoEntrega === 'RECEBER' && pedidoSemLogin && (!enderecoSemLogin || editandoEnderecoSemLogin) ? (
                <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                  <FormEndereco
                    semTipo
                    inicial={enderecoSemLogin ?? undefined}
                    titulo="Endereço de entrega"
                    alinharTitulo="left"
                    textoSalvar="Usar este endereço"
                    onSalvar={(endereco) => {
                      setEnderecoSemLogin(endereco);
                      setEditandoEnderecoSemLogin(false);
                    }}
                    onCancelar={enderecoSemLogin ? () => setEditandoEnderecoSemLogin(false) : undefined}
                  />
                </Box>
              ) : undefined
            }
          >
            {metodoEntrega === 'RECEBER' && (
              <Box sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
                {pedidoSemLogin ? (
                  !enderecoSemLogin || editandoEnderecoSemLogin ? null : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">
                          {enderecoSemLogin.logradouro}, {enderecoSemLogin.numero || 's/n'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {enderecoSemLogin.bairro}, {enderecoSemLogin.cidade}/{enderecoSemLogin.uf}
                        </Typography>
                      </Box>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => setEditandoEnderecoSemLogin(true)}>
                        Editar
                      </Button>
                    </Stack>
                  )
                ) : enderecos && enderecos.length > 0 ? (
                  editandoEndereco ? (
                    <>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={idEnderecoEntrega}
                        onChange={(e) => {
                          setIdEnderecoEntrega(Number(e.target.value));
                          setEditandoEndereco(false);
                        }}
                      >
                        {enderecos.map((e) => (
                          <MenuItem key={e.id} value={e.id}>
                            {e.apelido ? `${e.apelido} — ` : ''}
                            {e.logradouro}, {e.numero ?? 's/n'} ({e.bairro})
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button size="small" sx={{ mt: 1 }} onClick={() => navigate('/conta', { state: { de: '/checkout' } })}>
                        Gerenciar endereços
                      </Button>
                    </>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">
                          {enderecoSelecionado
                            ? `${enderecoSelecionado.logradouro}, ${enderecoSelecionado.numero ?? 's/n'}`
                            : 'Selecione um endereço'}
                        </Typography>
                        {enderecoSelecionado && (
                          <Typography variant="caption" color="text.secondary">
                            {enderecoSelecionado.bairro}, {enderecoSelecionado.cidade}/{enderecoSelecionado.uf}
                          </Typography>
                        )}
                      </Box>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => setEditandoEndereco(true)}>
                        Editar
                      </Button>
                    </Stack>
                  )
                ) : (
                  <Alert severity="warning">
                    Você ainda não tem endereço cadastrado.
                    {/* Botão empilhado abaixo do texto (não em `action`): em telas
                        estreitas o `action` não quebra linha e estourava o card. */}
                    <Box sx={{ mt: 1 }}>
                      <Button
                        color="inherit"
                        size="small"
                        variant="outlined"
                        onClick={() => navigate('/conta', { state: { de: '/checkout' } })}
                      >
                        Cadastrar endereço
                      </Button>
                    </Box>
                  </Alert>
                )}
                {statusTaxa.isLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      Calculando taxa…
                    </Typography>
                  </Box>
                )}
                {taxa && taxa.encontrado && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Taxa de entrega:{' '}
                    {freteDoPedido.freteComDesconto ? <FreteComDesconto frete={taxa.valor} cobrado={freteDoPedido.freteCobrado} /> : formatarBRL(taxa.valor)}
                    {taxa.distanciaKm != null && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}
                        · {taxa.distanciaKm.toString().replace('.', ',')} km
                      </Typography>
                    )}
                  </Typography>
                )}
                {foraDaArea && (
                  // Sem a ação aqui a mensagem manda fazer algo que a tela não
                  // oferece: com um endereço só cadastrado, o seletor não tem
                  // outra opção pra escolher.
                  <Alert
                    severity="warning"
                    sx={{ mt: 1 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={ajustarEndereco}
                      >
                        Trocar endereço
                      </Button>
                    }
                  >
                    {foraDaArea} Você também pode retirar no local.
                  </Alert>
                )}
                {taxa && !taxa.encontrado && taxa.estrategia === 'BAIRRO' && (
                  <Alert
                    severity="warning"
                    sx={{ mt: 1 }}
                    action={
                      <Button color="inherit" size="small" onClick={ajustarEndereco}>
                        Atualizar
                      </Button>
                    }
                  >
                    Não há taxa de entrega cadastrada para o bairro deste endereço. Selecione o bairro novamente no seu
                    endereço para continuar.
                  </Alert>
                )}
                {taxa && !taxa.encontrado && taxa.estrategia === 'KM' && (
                  <AvisoSemTaxaKm
                    motivo={taxa.motivo}
                    permiteRetirar={permiteRetirar}
                    whatsapp={loja?.configuracao.whatsapp ?? null}
                    onRetirar={() => setMetodoEntrega('RETIRAR')}
                    onConferirEndereco={ajustarEndereco}
                  />
                )}
              </Box>
            )}
          </CartaoOpcao>
        )}

        {permiteRetirar && (
          <CartaoOpcao
            selecionado={metodoEntrega === 'RETIRAR'}
            onClick={() => setMetodoEntrega('RETIRAR')}
            icone={<StorefrontIcon />}
            titulo="Retirar no estabelecimento"
          >
            {metodoEntrega === 'RETIRAR' && enderecoEmpresa && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {enderecoEmpresa.logradouro}
                {enderecoEmpresa.numero ? `, ${enderecoEmpresa.numero}` : ''}
                {enderecoEmpresa.bairro || enderecoEmpresa.cidade ? ' · ' : ''}
                {[enderecoEmpresa.bairro, enderecoEmpresa.cidade].filter(Boolean).join(', ')}
              </Typography>
            )}
          </CartaoOpcao>
        )}

        {/* Loja fechada e sem agendamento possível: não dá pra pedir agora. */}
        {fechadaSemPedido && (
          <Alert severity="warning" icon={<CalendarMonthIcon />}>
            Estamos fechados no momento. Faça seu pedido durante o horário de funcionamento.
          </Alert>
        )}

        {/* Agendamento (delivery) — conforme a config da loja. Com a loja fechada, é obrigatório. */}
        {agendamentoOfertavel && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            {precisaAgendar ? (
              <Alert severity="info" icon={<CalendarMonthIcon />} sx={{ mb: 1.5 }}>
                Estamos fechados agora — <strong>agende</strong> seu pedido para um horário disponível.
              </Alert>
            ) : (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CalendarMonthIcon color="action" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={600}>Agendar pedido</Typography>
                  {agendar && faixaSelecionada && diaSelecionado && (
                    <Typography variant="body2" color="text.secondary">
                      {diaSelecionado.rotulo}, às {faixaSelecionada.rotulo}
                    </Typography>
                  )}
                </Box>
                <Switch checked={agendar} onChange={(e) => setAgendar(e.target.checked)} />
              </Stack>
            )}
            <Collapse in={agendar || precisaAgendar}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: precisaAgendar ? 0 : 1.5 }}>
                <TextField
                  select
                  size="small"
                  label="Dia"
                  fullWidth
                  value={dataAgendada}
                  onChange={(e) => setDataAgendada(e.target.value)}
                >
                  {diasAgendaveis.map((d) => (
                    <MenuItem key={d.valor} value={d.valor}>
                      {d.rotulo}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Horário"
                  fullWidth
                  value={faixaAgendada}
                  onChange={(e) => setFaixaAgendada(e.target.value)}
                >
                  {(diaSelecionado?.faixas ?? []).map((f) => (
                    <MenuItem key={f.rotulo} value={f.rotulo}>
                      {f.rotulo}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Collapse>
          </Paper>
        )}
      </Stack>
    );
  }

  function renderConfirmacao(): JSX.Element {
    return (
      <Stack spacing={2} divider={<Divider flexItem />}>

        {/* Contato (nome + telefone) — ícone dentro do campo; logado, o nome é só texto */}
        <Stack spacing={2}>
          {pedidoSemLogin ? (
            <TextField
              label="Seu nome"
              value={nomeSemLogin}
              onChange={(e) => setNomeSemLogin(e.target.value)}
              fullWidth
              required
              autoComplete="name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <PersonIcon color="action" />
              <Typography fontWeight={600}>{usuario?.nome ?? '—'}</Typography>
            </Stack>
          )}
          <TextField
            label="Telefone para contato"
            type="tel"
            value={telefoneDelivery}
            onChange={(e) => setTelefoneDelivery(e.target.value)}
            fullWidth
            required
            autoComplete="tel"
            inputProps={{ inputMode: 'tel', enterKeyHint: 'done' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
            helperText="O restaurante usa este número para falar com você caso haja algum problema com o pedido."
          />
        </Stack>

        {/* Entrega / retirada + endereço + agendamento */}
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            {ehReceber ? <LocationOnIcon color="action" /> : <StorefrontIcon color="action" />}
            <Box>
              <Typography fontWeight={600}>
                {ehReceber ? 'Receber no endereço' : 'Retirar no estabelecimento'}
              </Typography>
              {ehReceber && enderecoSelecionado ? (
                <Typography variant="body2" color="text.secondary">
                  {enderecoSelecionado.logradouro}, {enderecoSelecionado.numero ?? 's/n'} — {enderecoSelecionado.bairro},{' '}
                  {enderecoSelecionado.cidade}/{enderecoSelecionado.uf}
                </Typography>
              ) : !ehReceber && enderecoEmpresa ? (
                <Typography variant="body2" color="text.secondary">
                  {enderecoEmpresa.logradouro}
                  {enderecoEmpresa.numero ? `, ${enderecoEmpresa.numero}` : ''}
                  {enderecoEmpresa.bairro || enderecoEmpresa.cidade ? ' — ' : ''}
                  {[enderecoEmpresa.bairro, enderecoEmpresa.cidade].filter(Boolean).join(', ')}
                </Typography>
              ) : null}
            </Box>
          </Stack>

          {agendar && faixaSelecionada && diaSelecionado && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CalendarMonthIcon color="action" />
              <Typography variant="body2">
                Agendado para {diaSelecionado.rotulo}, às {faixaSelecionada.rotulo}
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Forma de pagamento */}
        {formaPagamentoSel && (
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <IconePagamento forma={formaPagamentoSel.forma} />
            <Box>
              <Typography fontWeight={600}>{formaPagamentoSel.descricao}</Typography>
              {textoTroco() && (
                <Typography variant="body2" color="text.secondary">
                  {textoTroco()}
                </Typography>
              )}
            </Box>
          </Stack>
        )}

        {/* Itens — mesma listagem do carrinho, em leitura: quem confirma precisa
            ver os complementos que escolheu, não só o nome do produto. */}
        <ListaItensPedido itens={itensCarrinhoParaListagem(itens, imagensCatalogo, tiposCalculo)} />

        {/* Totais */}
        <Stack spacing={1}>
          <Linha label="Subtotal" valor={subtotal} />
          {ehReceber && (
            <Linha
              label="Taxa de entrega"
              valor={freteDoPedido.freteComDesconto ? <FreteComDesconto frete={taxa?.valor ?? 0} cobrado={taxaEntrega} /> : taxaEntrega}
            />
          )}
          {desconto > 0 && <Linha label="Desconto" valor={-desconto} />}
          {valorCashback > 0 && <Linha label="Cashback usado" valor={-valorCashback} />}
          <Divider />
          <Linha label="Total" valor={total} forte />
        </Stack>

        <LgpdAviso />
      </Stack>
    );
  }

  function renderWizard(): JSX.Element {
    if (passo === 0) return renderEntrega();
    if (passo === 1)
      return (
        <>
          {blocoCupom}
          <BlocoCashback
            cashback={cashbackNaTela}
            usuario={usuario}
            aoEntrar={() => setLoginAberto(true)}
            aoLigarOuDesligar={cashback.ligarOuDesligar}
          />
          {blocoPagamento}
        </>
      );
    return renderConfirmacao();
  }

  // ===== Conteúdo do TABLET (anônimo; mesa fixa do aparelho + comanda quando for o caso) =====
  function renderTablet(): JSX.Element {
    if (!mesaTablet) {
      return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography color="error" fontWeight={600} gutterBottom>
            Tablet não configurado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure a mesa deste tablet (rodapé → &quot;Configurar tablet&quot;) antes de fazer pedidos.
          </Typography>
        </Paper>
      );
    }
    return (
      <>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            {tabletModo === 'COMANDA' ? 'Comanda' : 'Pedido na mesa'}
          </Typography>

          <Stack spacing={2}>
            {tabletModo === 'COMANDA' ? (
              <CampoComanda
                rotulo="Número da comanda"
                valor={comanda}
                aoMudar={(v) => {
                  setComanda(v);
                  setErroComanda(null);
                }}
                erro={erroComanda}
                autoFocus
                inputRef={refComanda}
                helperText={`Mesa ${mesaTablet} · informe a comanda do cliente.`}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Pedido para a mesa {mesaTablet}.
              </Typography>
            )}
          </Stack>
        </Paper>
        {blocoResumoValores}
      </>
    );
  }

  // ===== Conteúdo de mesa/comanda (QR no celular do cliente) =====
  function renderLegacy(): JSX.Element {
    return (
      <>
        {/* Sem Paper: no celular o card em volta dos campos só empilha borda
            sobre borda — os próprios campos já delimitam o bloco. */}
        <Typography variant="subtitle1" gutterBottom>
          Seus dados
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Stack spacing={2} data-form-teclado>
            <TextField
              label="Nome"
              value={dadosMesa.nome}
              onChange={(e) => setDadosMesa((d) => ({ ...d, nome: e.target.value }))}
              onKeyDown={avancarNoEnter}
              fullWidth
              required
              autoComplete="name"
              inputRef={refNomeMesa}
              inputProps={{ enterKeyHint: 'next' }}
            />
            {(
              <TextField
                label="Telefone"
                type="tel"
                value={dadosMesa.telefone}
                onChange={(e) => setDadosMesa((d) => ({ ...d, telefone: e.target.value }))}
                onKeyDown={avancarNoEnter}
                fullWidth
                required
                autoComplete="tel"
                inputRef={refTelefoneMesa}
                inputProps={{ inputMode: 'tel', enterKeyHint: 'next' }}
              />
            )}
            <TextField
              label="CPF (opcional)"
              value={dadosMesa.cpf}
              onChange={(e) => setDadosMesa((d) => ({ ...d, cpf: e.target.value }))}
              onKeyDown={avancarNoEnter}
              fullWidth
              inputProps={{ inputMode: 'numeric', enterKeyHint: 'next' }}
            />
            {pedirPrincipalMesa ? (
              // QR ≠ principal (ex.: QR de mesa numa loja de comanda): a conta é a
              // comanda — pedimos o principal (o QR já preenche o secundário/mesa).
              <TextField
                label={`Número da ${ctxSalao.rotuloPrincipal} *`}
                value={identificadorSecundario}
                onChange={(e) => {
                  setIdentificadorSecundario(e.target.value);
                  setErroComanda(null);
                }}
                onKeyDown={avancarNoEnter}
                fullWidth
                required
                error={!!erroComanda}
                helperText={erroComanda ?? undefined}
                inputRef={refIdentificadorMesa}
                inputProps={{ inputMode: 'numeric', enterKeyHint: 'done' }}
              />
            ) : (
              secundario && secundario !== 'NENHUM' && (
                <TextField
                  label={`${secundario === 'MESA' ? 'Mesa' : 'Comanda'} ${secundarioObrigatorio ? '*' : '(opcional)'}`}
                  value={identificadorSecundario}
                  onChange={(e) => {
                    setIdentificadorSecundario(e.target.value);
                    setErroComanda(null);
                  }}
                  onKeyDown={avancarNoEnter}
                  fullWidth
                  required={secundarioObrigatorio}
                  error={!!erroComanda}
                  helperText={erroComanda ?? undefined}
                  inputRef={refIdentificadorMesa}
                  inputProps={{ inputMode: 'numeric', enterKeyHint: 'done' }}
                />
              )
            )}
            <LgpdAviso />
          </Stack>
        </Box>

        {/* Mesa/comanda não levam forma de pagamento nem cupom: o pedido chega
            na tela de mesa/comanda do Sischef e é acertado com o garçom. */}
        {blocoResumoValores}
      </>
    );
  }

  // ===== Footer (botão de ação) =====
  // Delivery e TOTEM usam o mesmo wizard de 3 passos; mesa/comanda seguem em página única.
  const ehWizard = ehDelivery;
  // Delivery: 3 passos (0..2). TOTEM: de 4 a 6, conforme pager e CPF na nota (ver ./passos-totem).
  const ultimoPasso = 2;
  const passoAtualValido =
    passo === 0 ? passo0Valido() : passo === 1 ? passo1Valido() : podeSubmeter();
  // Troco pendente no passo do pagamento: a ação do rodapé pergunta de novo em vez de ficar cinza.
  const passoPagamento = 1;
  const perguntarTroco = passo === passoPagamento && trocoPendente;

  // No totem e no tablet a ação do rodapé ganha a altura de toque do fluxo
  // (mesma do "Finalizar pedido" do carrinho); no celular o `size="large"` do
  // MUI já basta.
  const sxAcao = ehKiosk ? ESTILO_ACAO_KIOSK : undefined;

  const footer = ehWizard ? (
    passo < ultimoPasso ? (
      <Button
        variant="contained"
        fullWidth
        size="large"
        endIcon={<ChevronRightIcon />}
        disabled={!passoAtualValido && !perguntarTroco}
        onClick={() => (perguntarTroco ? setTrocoAberto(true) : setPasso((p) => p + 1))}
        sx={sxAcao}
      >
        Continuar
      </Button>
    ) : (
      <Button
        variant="contained"
        fullWidth
        size="large"
        // Totem com pager: o passo do pagamento já mostra "Enviar pedido" (ultimoPasso não conta o pager).
        disabled={(!podeSubmeter() && !perguntarTroco) || enviandoAoServidor || enviandoPedido}
        onClick={() => (perguntarTroco ? setTrocoAberto(true) : void enviarPedido())}
        sx={sxAcao}
      >
        {enviandoPedido || enviandoAoServidor ? <CircularProgress size={20} /> : 'Enviar pedido'}
      </Button>
    )
  ) : (
    <Button
      variant="contained"
      fullWidth
      size="large"
      disabled={!podeSubmeter() || enviandoAoServidor || enviandoPedido}
      onClick={() => void enviarPedido()}
      sx={sxAcao}
    >
      {enviandoPedido || enviandoAoServidor ? <CircularProgress size={20} /> : `Confirmar pedido · ${formatarBRL(total)}`}
    </Button>
  );

  const conteudo = (
    <>
      <Dialog
        open
        onClose={fechar}
        fullScreen={!ehDesktop || ehKiosk}
        fullWidth
        maxWidth={ehKiosk ? false : 'sm'}
        scroll="paper"
        PaperProps={{
          sx: {
            position: 'relative',
            ...(ehDesktop && !ehKiosk
              ? { height: '90vh', '@supports (height: 90dvh)': { height: '90dvh' } }
              : {}),
            ...(ehKiosk ? { bgcolor: 'background.default' } : {}),
          },
        }}
      >
        <DialogTitle sx={{ bgcolor: 'background.paper', p: 0 }}>
          <ScreenHeader
            title="Finalizar pedido"
            subTitle={`${totalItens} ${totalItens === 1 ? 'item' : 'itens'} · ${formatarBRL(total)}`}
            onClickBack={ehWizard && passo > 0 ? () => setPasso((p) => p - 1) : fechar}
            logoUrl={loja.configuracao.urlLogo}
            alturaLogo={medidasDoAparelho(loja.identificador).alturaLogo}
            fonteTitulo={medidasDoAparelho(loja.identificador).fonteTitulo}
            fonteSubtitulo={medidasDoAparelho(loja.identificador).fonteSubtitulo}
          />
        </DialogTitle>

        <DialogContent dividers sx={{ pb: 2, ...(ehKiosk ? { px: 2 } : {}) }}>
          <Box sx={ehKiosk ? { mx: 'auto', width: '100%' } : undefined}>
            {ehWizard && (
              <Stepper activeStep={passo} alternativeLabel sx={{ mb: 3, mt: 1 }}>
                {PASSOS.map((p) => (
                  <Step key={p}>
                    <StepLabel>{p}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            {ehDelivery ? renderWizard() : ehTablet ? renderTablet() : renderLegacy()}
          </Box>

          {erro && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {erro}
            </Alert>
          )}

          {avisoDoEnvio && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {avisoDoEnvio}
            </Alert>
          )}

          {/* Só no passo de enviar: é onde fica o "Enviar pedido" que o aviso manda tocar. */}
          {cashback.motivoDaTentativa && passo === ultimoPasso && <AvisoCashbackPendente motivo={cashback.motivoDaTentativa} />}

          {falhaEnvio && (
            <Button variant="outlined" fullWidth startIcon={<HomeIcon />} onClick={() => navigate('/catalogo')} sx={{ mt: 2 }}>
              Voltar ao início
            </Button>
          )}
        </DialogContent>

        <Box
          data-teclado-acao
          sx={{
            p: 2,
            flexShrink: 0,
            ...(ehKiosk ? { pb: 'calc(16px + env(safe-area-inset-bottom))' } : {}),
          }}
        >
          <Box sx={ehKiosk ? { mx: 'auto', width: '100%' } : undefined}>
            {ehDelivery && passo === 2 && !accessToken && !pedidoSemLogin && (
              <Paper
                variant="outlined"
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  borderColor: 'primary.main',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                }}
              >
                <PersonIcon color="primary" fontSize="small" />
                <Typography variant="body2" sx={{ flexGrow: 1, lineHeight: 1.3 }}>
                  Entre na sua conta para finalizar o pedido.
                </Typography>
                <Button size="small" variant="contained" disableElevation onClick={() => setLoginAberto(true)} sx={{ flexShrink: 0 }}>
                  Entrar
                </Button>
              </Paper>
            )}
            {!escolhendoComoPedir && footer}
          </Box>
        </Box>
      </Dialog>

      {trocoAberto && (
        <DialogTroco
          aberto
          total={total}
          trocoInicial={trocoInput}
          onFechar={() => setTrocoAberto(false)}
          onOutraForma={() => {
            setIdFormaPagamento('');
            setTrocoAberto(false);
          }}
          onConfirmar={(troco) => {
            setTrocoInput(troco);
            setTrocoRespondido(true);
            setTrocoAberto(false);
          }}
        />
      )}

      {onlineAberto && (
        <DialogPagamentoOnline
          aberto
          pedidoId={onlineAberto.pedidoId}
          valorCentavos={onlineAberto.valorCentavos}
          metodos={onlineAberto.metodos}
          onSucesso={() => concluirPagamentoOnline(onlineAberto.pedidoId, onlineAberto.valorCentavos)}
          onCancelar={() => setConfirmarCancelOnline(true)}
          onRefazer={() => {
            // Pedido cancelado/expirado: reusar a chave devolveria ele de novo.
            descartarChave()
            setOnlineAberto(null)
          }}
          onOutraForma={() => void escolherOutraForma()}
          ocupado={cancelandoOnline || trocandoForma}
        />
      )}

      {/* Cancelar o pagamento online: cancela o pedido no backend, zera o carrinho e volta pro cardápio. */}
      <DialogCancelarPagamento
        aberto={confirmarCancelOnline}
        titulo="Cancelar o pedido?"
        descricao="O pedido não vai para a loja e os itens saem do carrinho. Para pagar de outro jeito, toque em Voltar e escolha outra forma de pagamento."
        cancelando={cancelandoOnline}
        onVoltar={() => setConfirmarCancelOnline(false)}
        onConfirmar={() => void confirmarCancelamentoOnline()}
      />

      {/* Login sem sair do checkout: ao concluir, fecha e o cliente continua no
        mesmo passo (o token na store reativa as queries de usuário/endereços). */}
      <DialogLogin aberto={loginAberto} onFechar={() => setLoginAberto(false)} onSucesso={() => setLoginAberto(false)} />
    </>
  );

  return ehKiosk ? <TemaKiosk>{conteudo}</TemaKiosk> : conteudo;
}

function CartaoOpcao({
  selecionado,
  onClick,
  icone,
  titulo,
  children,
  corpoLargo,
}: {
  selecionado: boolean;
  onClick: () => void;
  icone: JSX.Element;
  titulo: string;
  children?: ReactNode;
  /** Conteúdo na largura toda do cartão (ex.: formulário), abaixo do título. */
  corpoLargo?: ReactNode;
}): JSX.Element {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        cursor: 'pointer',
        borderColor: selecionado ? 'primary.main' : 'divider',
        borderWidth: selecionado ? 2 : 1,
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box sx={{ color: 'text.secondary', display: 'flex', mt: 0.25 }}>{icone}</Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography fontWeight={600}>{titulo}</Typography>
          {children}
        </Box>
        <Radio checked={selecionado} />
      </Stack>
      {corpoLargo}
    </Paper>
  );
}

/**
 * O aviso de quando a taxa por distância não saiu.
 *
 * Era uma mensagem só para quatro causas, e ela mandava "confira se o endereço
 * está completo" mesmo quando o endereço estava perfeito e o problema era o
 * cadastro da loja. O cliente conferia, voltava e via o mesmo texto — rótulo
 * que mente é pior que rótulo ausente, porque ausente a pessoa pergunta.
 *
 * Cada motivo agora oferece a ação que de fato resolve, e só ela.
 */
function AvisoSemTaxaKm({
  motivo,
  permiteRetirar,
  whatsapp,
  onRetirar,
  onConferirEndereco,
}: {
  motivo?: MotivoSemTaxa;
  permiteRetirar: boolean;
  whatsapp: string | null;
  onRetirar: () => void;
  onConferirEndereco: () => void;
}): JSX.Element {
  const contato = whatsapp?.trim();

  // Culpa da loja: o cliente não tem o que consertar, e mandar ele mexer no
  // endereço só gasta a paciência dele antes de ligar para a loja.
  if (motivo === 'LOJA_SEM_ENTREGA') {
    const acao = permiteRetirar ? (
      <Button color="inherit" size="small" onClick={onRetirar}>
        Retirar no local
      </Button>
    ) : contato ? (
      <Button color="inherit" size="small" href={montarLinkWhatsapp(contato)} target="_blank" rel="noopener">
        Falar com a loja
      </Button>
    ) : undefined;

    return (
      <Alert severity="warning" sx={{ mt: 1 }} action={acao}>
        Esta loja ainda não ativou a entrega por aqui. Não é problema com o seu endereço —{' '}
        {permiteRetirar ? 'você pode retirar no local.' : 'fale com a loja para fazer seu pedido.'}
      </Alert>
    );
  }

  // Endereço válido, mas sem caminho de carro. Trocar de endereço é a única
  // saída, e não adianta pedir para "completar" o que já está completo.
  if (motivo === 'SEM_ROTA') {
    return (
      <Alert
        severity="warning"
        sx={{ mt: 1 }}
        action={
          <Button color="inherit" size="small" onClick={onConferirEndereco}>
            Trocar endereço
          </Button>
        }
      >
        Não encontramos um caminho de carro até esse endereço
        {permiteRetirar ? '. Você também pode retirar no local' : ''}.
      </Alert>
    );
  }

  // Sobra o endereço que o mapa não localizou — aqui, sim, conferir resolve.
  return (
    <Alert
      severity="warning"
      sx={{ mt: 1 }}
      action={
        <Button color="inherit" size="small" onClick={onConferirEndereco}>
          Conferir endereço
        </Button>
      }
    >
      Não encontramos esse endereço no mapa, então não dá para calcular a entrega. Confira a rua, o número e o CEP
      {permiteRetirar ? ', ou escolha retirar no local' : ''}.
    </Alert>
  );
}

function Linha({ label, valor, forte }: { label: string; valor: number | JSX.Element; forte?: boolean }): JSX.Element {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography fontWeight={forte ? 'bold' : 'normal'}>{label}</Typography>
      <Typography fontWeight={forte ? 'bold' : 'normal'}>{typeof valor === 'number' ? formatarBRL(valor) : valor}</Typography>
    </Stack>
  );
}

function FreteComDesconto({ frete, cobrado }: { frete: number; cobrado: number }): JSX.Element {
  return (
    <>
      <Box component="s" sx={{ color: 'text.secondary' }}>{formatarBRL(frete)}</Box> {cobrado > 0 ? formatarBRL(cobrado) : 'Grátis'}
    </>
  )
}

/** Código de domínio do erro (ex.: FORA_DA_AREA) para tratar caso a caso. */
function codigoDoErro(err: unknown): string | null {
  if (typeof err === 'object' && err && 'data' in err) {
    const d = (err as { data?: { codigo?: string } }).data;
    return d?.codigo ?? null;
  }
  return null;
}

function extrairMsg(err: unknown): string | null {
  if (typeof err === 'object' && err && 'data' in err) {
    const d = (err as { data?: { mensagem?: string } }).data;
    return d?.mensagem ?? null;
  }
  return null;
}

/** Métodos TEF disponíveis na forma: usa o CSV `metodos`; senão deriva da natureza. */
